// Helper utilities for making authenticated API calls to backend

interface ApiResponse<T = any> {
    success: boolean;
    error?: string;
    data?: T;
    [key: string]: any;
}

// Get auth token from localStorage (managed by authService)
export function getAuthToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('auth_token');
}

// Get admin token from localStorage (managed by admin pages)
export function getAdminToken(): string | null {
    if (typeof window === 'undefined') return null;
    try {
        const stored = localStorage.getItem('pb_admin_auth');
        if (stored) {
            const parsed = JSON.parse(stored);
            return parsed.token || null;
        }
    } catch (e) {
        return null;
    }
    return null;
}

// Make authenticated API call
export async function apiCall<T = any>(
    endpoint: string,
    options: RequestInit = {}
): Promise<ApiResponse<T>> {
    const token = getAuthToken();

    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string> || {}),
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    try {
        const response = await fetch(endpoint, {
            ...options,
            headers,
            credentials: 'include', // Important! Allows cookies
        });

        const data = await response.json();

        if (!response.ok) {
            return {
                success: false,
                error: data.error || 'Request failed',
                ...data
            };
        }

        return {
            success: true,
            ...data
        };
    } catch (error: any) {
        return {
            success: false,
            error: error.message || 'Network error'
        };
    }
}

// Authentication API calls
export const authApi = {
    login: async (email: string, password: string) => {
        return apiCall('/api/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, password })
        });
    },

    register: async (email: string, password: string, passwordConfirm: string, name: string, termsAcceptance: any) => {
        return apiCall('/api/register', {
            method: 'POST',
            body: JSON.stringify({ email, password, passwordConfirm, name, termsAcceptance })
        });
    },

    requestPasswordReset: async (email: string) => {
        return apiCall('/api/auth/request-password-reset', {
            method: 'POST',
            body: JSON.stringify({ email })
        });
    },

    confirmPasswordReset: async (token: string, password: string, passwordConfirm: string) => {
        return apiCall('/api/auth/confirm-password-reset', {
            method: 'POST',
            body: JSON.stringify({ token, password, passwordConfirm })
        });
    },

    verifyEmail: async (token: string) => {
        return apiCall('/api/auth/verify-email', {
            method: 'POST',
            body: JSON.stringify({ token })
        });
    },

    requestVerification: async (email: string) => {
        return apiCall('/api/auth/request-verification', {
            method: 'POST',
            body: JSON.stringify({ email })
        });
    },

    refresh: async (token: string) => {
        return apiCall('/api/auth/refresh', {
            method: 'POST',
            body: JSON.stringify({ token })
        });
    }
};

// User API calls
export const userApi = {
    getProfile: async () => {
        return apiCall('/api/user/profile', {
            method: 'GET'
        });
    },

    updateProfile: async (updates: any) => {
        return apiCall('/api/user/profile', {
            method: 'PATCH',
            body: JSON.stringify(updates)
        });
    },

    // Server mints the new token; nothing is sent in the body by design.
    regenerateApiToken: async () => {
        return apiCall('/api/user/api-token/regenerate', {
            method: 'POST'
        });
    },

    getPreferences: async () => {
        return apiCall('/api/user/preferences', {
            method: 'GET'
        });
    },

    createPreferences: async (preferences: any) => {
        return apiCall('/api/user/preferences', {
            method: 'POST',
            body: JSON.stringify(preferences)
        });
    },

    updatePreferences: async (id: string, updates: any) => {
        return apiCall('/api/user/preferences', {
            method: 'PATCH',
            body: JSON.stringify({ id, ...updates })
        });
    }
};

// Apps API calls
export const appsApi = {
    getAll: async () => {
        return apiCall('/api/apps', {
            method: 'GET'
        });
    },

    getBySlug: async (slug: string) => {
        return apiCall(`/api/apps?slug=${slug}`, {
            method: 'GET'
        });
    }
};

// Trials API calls
export const trialsApi = {
    getAll: async (appId?: string, appSlug?: string) => {
        let url = '/api/trials';
        const params = new URLSearchParams();
        if (appId) params.append('app_id', appId);
        if (appSlug) params.append('app_slug', appSlug);
        if (params.toString()) url += `?${params.toString()}`;

        return apiCall(url, {
            method: 'GET'
        });
    },

    create: async (appId: string, durationDays?: number) => {
        return apiCall('/api/trials', {
            method: 'POST',
            body: JSON.stringify({ app_id: appId, duration_days: durationDays })
        });
    },

    // New: create a single level-based trial (one record covers all apps in the level)
    createForLevel: async (level: number, durationDays?: number) => {
        return apiCall('/api/trials', {
            method: 'POST',
            body: JSON.stringify({ level, duration_days: durationDays })
        });
    },
};

// Plans API calls
export const plansApi = {
    getAll: async () => {
        return apiCall('/api/plans', {
            method: 'GET'
        });
    }
};

// Blog API calls
export const blogApi = {
    getPosts: async (page: number = 1, perPage: number = 20) => {
        return apiCall(`/api/blog/posts?page=${page}&per_page=${perPage}`, {
            method: 'GET'
        });
    },

    getPost: async (slug: string) => {
        return apiCall(`/api/blog/posts?slug=${slug}`, {
            method: 'GET'
        });
    },

    getComments: async (postId: string, page: number = 1, perPage: number = 50) => {
        return apiCall(`/api/blog/comments?post_id=${postId}&page=${page}&per_page=${perPage}`, {
            method: 'GET'
        });
    },

    createComment: async (postId: string, content: string) => {
        return apiCall('/api/blog/comments', {
            method: 'POST',
            body: JSON.stringify({ post_id: postId, content })
        });
    }
};

// Notifications API calls
export const notificationsApi = {
    getAll: async (page: number = 1, perPage: number = 50, unreadOnly: boolean = false) => {
        return apiCall(
            `/api/notifications?page=${page}&per_page=${perPage}&unread_only=${unreadOnly}`,
            { method: 'GET' }
        );
    },

    markAsRead: async (notificationId: string) => {
        return apiCall('/api/notifications', {
            method: 'PATCH',
            body: JSON.stringify({ notification_id: notificationId })
        });
    },

    markAllAsRead: async () => {
        return apiCall('/api/notifications', {
            method: 'PATCH',
            body: JSON.stringify({ mark_all_read: true })
        });
    },

    deleteNotification: async (notificationId: string) => {
        return apiCall(`/api/notifications?notification_id=${notificationId}`, {
            method: 'DELETE'
        });
    }
};
